import { ArrowDownOutlined, ArrowLeftOutlined, ArrowUpOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  message,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { queryErrorMessage } from '../CashTransactions/types';
import {
  IMPORT_ACTION_TYPE_LABELS,
  IMPORT_ITEM_ACTION_TAGS,
  IMPORT_STATUS_TAGS,
  type ImportDetail,
  type ImportItem,
  type ImportItemAction,
  participantName,
} from './types';

const { Title } = Typography;

const formatChanges = (changes: Record<string, string> | null) =>
  changes
    ? Object.entries(changes)
        .map(([field, change]) => `${field}: ${change}`)
        .join('\n')
    : '';

// One Participant import's comparison. "Sync" writes the new and changed rows
// all at once (error rows are left out); "Цуцлах" discards the import.
export default function ParticipantImportCompare({ importId }: { importId: number }) {
  const { appKey } = useParams<{ appKey: string }>();
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [detail, setDetail] = useState<ImportDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<'sync' | 'cancel' | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<ImportDetail>('competition_import_detail', { importId })
      .then(([row]) => setDetail(row ?? null))
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, importId]);

  useEffect(load, [load]);

  const act = async (action: 'sync' | 'cancel') => {
    setWorking(action);
    try {
      const [row] = await runQuery<{ created: number; updated: number }>(
        action === 'sync' ? 'competition_import_sync' : 'competition_import_cancel',
        { importId },
      );
      if (action === 'cancel') message.success('Импорт цуцлагдлаа.');
      else {
        message.success(
          `Амжилттай: ${row?.created ?? 0} шинээр нэмэгдэж, ${row?.updated ?? 0} шинэчлэгдлээ.`,
        );
      }
      load();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setWorking(null);
    }
  };

  const open = detail?.status === 'COMPARE';
  const writable = (detail?.created_record ?? 0) + (detail?.updated_record ?? 0);

  const columns: ColumnsType<ImportItem> = [
    { title: 'Мөр', dataIndex: 'row_number', width: 60 },
    { title: 'Түлхүүр', dataIndex: 'submission_key', width: 140 },
    { title: 'Овог нэр', render: (_, row) => participantName(row) || '—' },
    {
      title: 'Анги',
      dataIndex: 'competition_class_year',
      width: 100,
      render: (v: string | null, row) => row.class_year_name ?? v ?? '—',
    },
    {
      title: 'Утас',
      dataIndex: 'mobile_no',
      width: 110,
      render: (v: string[] | null) => (v?.length ? v.join(', ') : '—'),
    },
    { title: 'Өрөө', dataIndex: 'exam_room', width: 80, render: (v: string | null) => v || '—' },
    {
      title: 'Дугаар',
      dataIndex: 'exam_number',
      width: 80,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Үйлдэл',
      dataIndex: 'action',
      width: 160,
      filters: (Object.keys(IMPORT_ITEM_ACTION_TAGS) as ImportItemAction[]).map((action) => ({
        text: IMPORT_ITEM_ACTION_TAGS[action].label,
        value: action,
      })),
      onFilter: (value, row) => row.action === value,
      render: (value: ImportItemAction) => (
        <Tag color={IMPORT_ITEM_ACTION_TAGS[value].color}>
          {IMPORT_ITEM_ACTION_TAGS[value].label}
        </Tag>
      ),
    },
    {
      title: 'Өөрчлөлт / Тайлбар',
      render: (_, row) => (
        <span style={{ whiteSpace: 'pre-line' }}>
          {formatChanges(row.changes)}
          {row.changes && row.message ? '\n' : ''}
          {row.message && (
            <span style={{ color: row.action === 'ERROR' ? '#cf1322' : '#d48806' }}>
              {row.message}
            </span>
          )}
          {!row.changes && !row.message && '—'}
        </span>
      ),
    },
  ];

  const stat = (title: string, value: number, color: string, arrow?: 'up' | 'down') => (
    <Col flex={1}>
      <Card variant="borderless" style={{ borderRadius: 12 }}>
        <Statistic
          title={title}
          value={value}
          valueStyle={{ color }}
          prefix={
            value > 0 && arrow ? (
              arrow === 'up' ? (
                <ArrowUpOutlined />
              ) : (
                <ArrowDownOutlined />
              )
            ) : undefined
          }
        />
      </Card>
    </Col>
  );

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card loading={loading && !detail}>
        <Link to={`/apps/${appKey}/competition_participant_import`}>
          <ArrowLeftOutlined /> Импортын жагсаалт руу буцах
        </Link>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '16px 0',
          }}
        >
          <Space wrap>
            <Title level={4} style={{ margin: 0 }}>
              Харьцуулах
            </Title>
            {detail && (
              <>
                <span>
                  {detail.competition_name} · {detail.file_name}
                </span>
                <Tag color={IMPORT_STATUS_TAGS[detail.status]?.color}>
                  {IMPORT_STATUS_TAGS[detail.status]?.label}
                </Tag>
                <Tag>{IMPORT_ACTION_TYPE_LABELS[detail.action_type]}</Tag>
              </>
            )}
          </Space>

          {canEdit && (
            <Space>
              {open && (
                <Popconfirm
                  title="Импортыг цуцлах уу?"
                  description="Оролцогч нэмэгдэхгүй, өөрчлөгдөхгүй."
                  okText="Цуцлах"
                  okButtonProps={{ danger: true }}
                  cancelText="Болих"
                  onConfirm={() => act('cancel')}
                >
                  <Button danger loading={working === 'cancel'} disabled={working != null}>
                    Цуцлах
                  </Button>
                </Popconfirm>
              )}
              <Popconfirm
                title="Sync хийх үү?"
                description={`${detail?.created_record ?? 0} оролцогч шинээр нэмэгдэж, ${detail?.updated_record ?? 0} шинэчлэгдэнэ.${detail?.error_record ? ` Алдаатай ${detail.error_record} мөр орохгүй.` : ''} Буцаах боломжгүй.`}
                okText="Тийм, Sync хийх"
                cancelText="Болих"
                onConfirm={() => act('sync')}
                disabled={!open || !writable}
              >
                <Button
                  type="primary"
                  loading={working === 'sync'}
                  disabled={!open || !writable || working != null}
                >
                  {detail?.status === 'DONE' ? 'Sync хийгдсэн' : 'Sync'}
                </Button>
              </Popconfirm>
            </Space>
          )}
        </div>

        {detail && (
          <Row
            gutter={16}
            style={{ marginBottom: 24, background: '#f5f5f7', padding: 16, borderRadius: 12 }}
          >
            {stat('Нийт', detail.total_record, '#262626')}
            {stat('Шинээр нэмэгдэх', detail.created_record, '#389e0d', 'up')}
            {stat('Шинэчлэгдэх', detail.updated_record, '#1677ff', 'up')}
            {stat('Алдаатай', detail.error_record, '#cf1322', 'down')}
            {stat('Алгасагдана', detail.skipped_record, '#8c8c8c')}
          </Row>
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={detail?.items ?? []}
          loading={loading}
          scroll={{ x: true }}
          pagination={{ pageSize: 50 }}
        />
      </Card>
    </LayoutWithMenu>
  );
}
